import { useEffect, useCallback, useRef } from "react";
import { useCopilotChat } from "@zapier/ai-toolkit/client";
import { useCopilotDrawerState } from "@zapier/canvas-state";
import { buildInformedAssistantMessage } from "@zapier/canvas-state";
import { Message } from "@ai-sdk/ui-utils";
import { CopilotUserMessage } from "@zapier/canvas-state";

/**
 * Hook to listen for postMessage events from copilot tool components
 * and inject them into the chat conversation
 */
export function useCopilotToolMessageListener() {
  const copilot = useCopilotChat();
  const [isCopilotOpen, setIsCopilotOpen] = useCopilotDrawerState();

  // Use ref to always access latest copilot without triggering effect re-runs
  const copilotRef = useRef(copilot);
  copilotRef.current = copilot;

  // Memoize the handler to prevent unnecessary event listener churn
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      // Only handle our specific message type
      if (event.data?.type !== "COPILOT_USER_MESSAGE") {
        return;
      }

      const messageData = event.data as CopilotUserMessage;
      const { source } = messageData.payload;

      // Access current copilot from ref
      const currentCopilot = copilotRef.current;

      if (!currentCopilot?.setMessages) {
        return;
      }

      // Open Copilot sidebar if it's not already open
      if (!isCopilotOpen) {
        setIsCopilotOpen(true);
      }

      let nextMessages: Message[] = [];
      let userMessage: string;

      // Handle different message types
      switch (messageData.payload.messageType) {
        case "text":
          userMessage = messageData.payload.message;
          break;
        case "error":
          nextMessages = buildInformedAssistantMessage({
            isError: true,
            errorMessage: messageData.payload.message,
          });
          userMessage = "Fix the error in my canvas.";
          break;
        case "success":
          nextMessages = buildInformedAssistantMessage({
            isError: false,
            successMessage: messageData.payload.message,
          });
          userMessage = messageData.payload.message;
          break;
        default:
          return;
      }

      // Add message to the chat conversation
      currentCopilot.setMessages((prevMessages) => [
        ...prevMessages,
        ...nextMessages,
        {
          role: "user" as const,
          content: userMessage,
          id: `tool-message-${Date.now()}-${source}`,
          createdAt: new Date(),
          parts: [{ type: "text" as const, text: userMessage }],
        },
      ]);

      // Trigger LLM response
      if (currentCopilot.reload) {
        void currentCopilot.reload();
      }
    },
    [isCopilotOpen, setIsCopilotOpen],
  );

  useEffect(() => {
    // Add event listener
    window.addEventListener("message", handleMessage);

    // Cleanup
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [handleMessage]);
}
